import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UrgentNotifications = ({ notifications, onViewAll }) => {
  const navigate = useNavigate();

  const getNotificationIcon = (priority) => {
    switch (priority) {
      case 'critical':
        return { name: 'AlertCircle', color: 'var(--color-error)', bgColor: 'bg-error/10' };
      case 'high':
        return { name: 'AlertTriangle', color: 'var(--color-warning)', bgColor: 'bg-warning/10' };
      default:
        return { name: 'Info', color: 'var(--color-accent)', bgColor: 'bg-accent/10' };
    }
  };

  const handleNotificationClick = (notification) => {
  const notificationType = notification?.type || notification?.category;
  const siteId = notification?.site_id;
  
  // Check both the old fields and the new target fields
  const documentId = notification?.document_id || (notification?.target_type === 'document' ? notification?.target_id : null);
  const incidentId = notification?.incident_id || (notification?.target_type === 'incident' ? notification?.target_id : null);
  const checklistId = notification?.checklist_id || (notification?.target_type === 'checklist' ? notification?.target_id : null);
  
  // Deep-link to specific document detail page
  if (documentId && siteId) {
    navigate(`/sites/${siteId}/documents/${documentId}`);
    return;
  }
    
    // Deep-link to site with specific tab and item highlighting
    if (siteId) {
      let targetTab = 'documents'; // default
      let itemId = null;
      
      if (notificationType === 'incident' || notification?.title?.toLowerCase()?.includes('incident')) {
        targetTab = 'incidents';
        itemId = incidentId;
      } else if (notificationType === 'checklist' || notification?.title?.toLowerCase()?.includes('checklist') || notification?.title?.toLowerCase()?.includes('task')) {
        targetTab = 'daily-tasks';
        itemId = checklistId;
      } else if (notificationType === 'audit' || notification?.title?.toLowerCase()?.includes('audit')) {
        targetTab = 'audits';
      } else if (notificationType === 'drawing' || notification?.title?.toLowerCase()?.includes('drawing')) {
        targetTab = 'drawings';
      } else if (notificationType === 'document' || notification?.title?.toLowerCase()?.includes('document') || notification?.title?.toLowerCase()?.includes('expir')) {
        targetTab = 'documents';
      }
      
      // Build query string with tab and optional item highlighting
      const queryParams = new URLSearchParams({ tab: targetTab });
      if (itemId) {
        queryParams?.append('highlight', itemId);
      }
      
      navigate(`/sites/${siteId}?${queryParams?.toString()}`);
      return;
    }
    
    // Fallback: navigate to notifications center
    navigate('/notifications-center');
  };

  const handleViewAll = () => {
    navigate('/notifications-center');
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground font-heading">Urgent Notifications</h3>
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-6 h-6 bg-error text-error-foreground rounded-full text-xs font-bold">
            {notifications?.length}
          </span>
        </div>
      </div>
      <div className="space-y-3 mb-4">
        {notifications?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Icon name="CheckCircle2" size={48} className="text-success mb-3" />
            <p className="text-muted-foreground text-center">No urgent notifications</p>
          </div>
        ) : (
          notifications?.slice(0, 5)?.map((notification) => {
            const iconConfig = getNotificationIcon(notification?.priority);
            return (
              <div
                key={notification?.id}
                onClick={() => handleNotificationClick(notification)}
                className={`flex gap-3 p-3 rounded-lg ${iconConfig?.bgColor} border border-border cursor-pointer hover:shadow-md transition-shadow`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e?.key === 'Enter' || e?.key === ' ') {
                    e?.preventDefault();
                    handleNotificationClick(notification);
                  }
                }}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <Icon name={iconConfig?.name} size={20} color={iconConfig?.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{notification?.title}</p>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1 line-clamp-2">
                    {notification?.message}
                  </p>
                  <p className="text-xs text-muted-foreground font-caption mt-1">
                    {notification?.timestamp}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
      {notifications?.length > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleViewAll}
          iconName="ArrowRight"
          iconPosition="right"
          fullWidth
        >
          View All Notifications
        </Button>
      )}
    </div>
  );
};

export default UrgentNotifications;